import { useState, useEffect } from "react";
import { apiFetch } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Pencil, Trash2, Plus, ArrowLeft, Star, Eye, EyeOff, ArrowUp, ArrowDown, RefreshCw } from "lucide-react";

interface Review {
  id: number;
  name: string;
  subtitle: string | null;
  text: string;
  rating: number;
  imagePath: string | null;
  source: string;
  googleReviewId: string | null;
  featured: boolean;
  sortOrder: number;
  createdAt: string;
}

export default function ReviewManager() {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [editing, setEditing] = useState<Review | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [fetchingGoogle, setFetchingGoogle] = useState(false);
  const [googleMessage, setGoogleMessage] = useState("");

  const [name, setName] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [text, setText] = useState("");
  const [rating, setRating] = useState(5);
  const [imagePath, setImagePath] = useState("");
  const [featured, setFeatured] = useState(true);

  useEffect(() => {
    loadReviews();
  }, []);

  async function loadReviews() {
    try {
      const data = await apiFetch("/reviews/all");
      setReviews(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function startNew() {
    setEditing(null);
    setIsNew(true);
    setName("");
    setSubtitle("");
    setText("");
    setRating(5);
    setImagePath("");
    setFeatured(true);
    setError("");
  }

  function startEdit(review: Review) {
    setEditing(review);
    setIsNew(false);
    setName(review.name);
    setSubtitle(review.subtitle || "");
    setText(review.text);
    setRating(review.rating);
    setImagePath(review.imagePath || "");
    setFeatured(review.featured);
    setError("");
  }

  function cancelEdit() {
    setEditing(null);
    setIsNew(false);
    setError("");
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      if (isNew) {
        await apiFetch("/reviews", {
          method: "POST",
          body: JSON.stringify({ name, subtitle, text, rating, imagePath, featured }),
        });
      } else if (editing) {
        await apiFetch(`/reviews/${editing.id}`, {
          method: "PUT",
          body: JSON.stringify({ name, subtitle, text, rating, imagePath, featured }),
        });
      }
      cancelEdit();
      await loadReviews();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: number) {
    if (!window.confirm("Delete this review?")) return;
    try {
      await apiFetch(`/reviews/${id}`, { method: "DELETE" });
      await loadReviews();
    } catch (err: any) {
      setError(err.message);
    }
  }

  async function toggleFeatured(review: Review) {
    try {
      await apiFetch(`/reviews/${review.id}`, {
        method: "PUT",
        body: JSON.stringify({ featured: !review.featured }),
      });
      await loadReviews();
    } catch (err: any) {
      setError(err.message);
    }
  }

  async function moveReview(index: number, direction: "up" | "down") {
    const newReviews = [...reviews];
    const swap = direction === "up" ? index - 1 : index + 1;
    [newReviews[index], newReviews[swap]] = [newReviews[swap], newReviews[index]];
    setReviews(newReviews);
    try {
      await apiFetch("/reviews/reorder", {
        method: "PUT",
        body: JSON.stringify({ orders: newReviews.map((r) => ({ id: r.id })) }),
      });
    } catch (err: any) {
      setError(err.message);
      await loadReviews();
    }
  }

  async function fetchGoogleReviews() {
    setFetchingGoogle(true);
    setGoogleMessage("Fetching from Google Business Profile…");
    try {
      const result = await apiFetch("/reviews/fetch-google", { method: "POST" });
      setGoogleMessage(result.message + (result.totalReviews ? ` (${result.totalReviews} total on Google)` : ""));
      if (result.imported > 0) await loadReviews();
    } catch (err: any) {
      setGoogleMessage(`Error: ${err.message}`);
    } finally {
      setFetchingGoogle(false);
    }
  }

  if (loading) {
    return <p className="text-muted-foreground p-4">Loading reviews...</p>;
  }

  if (isNew || editing) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <button onClick={cancelEdit} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4">
          <ArrowLeft className="w-4 h-4" /> Back to Reviews
        </button>
        <h2 className="text-xl font-serif font-bold mb-6">
          {isNew ? "Add Review" : "Edit Review"}
        </h2>
        {error && <div className="mb-4 p-3 bg-red-50 text-red-600 text-sm rounded-md">{error}</div>}
        <form onSubmit={handleSave} className="space-y-4 max-w-2xl">
          <div>
            <Label htmlFor="name">Reviewer Name *</Label>
            <Input id="name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Catherine R." required />
          </div>
          <div>
            <Label htmlFor="subtitle">Subtitle (pet name, etc.)</Label>
            <Input id="subtitle" value={subtitle} onChange={(e) => setSubtitle(e.target.value)} placeholder="e.g. George, Springer Spaniel" />
          </div>
          <div>
            <Label htmlFor="text">Review Text *</Label>
            <textarea
              id="text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              className="w-full min-h-[150px] p-3 border rounded-md text-sm"
              placeholder="The review text..."
              required
            />
          </div>
          <div>
            <Label htmlFor="rating">Rating</Label>
            <div className="flex items-center gap-2 mt-1">
              {[1, 2, 3, 4, 5].map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setRating(v)}
                  className="focus:outline-none"
                >
                  <Star
                    size={24}
                    className={v <= rating ? "text-yellow-400" : "text-gray-300"}
                    fill={v <= rating ? "currentColor" : "none"}
                  />
                </button>
              ))}
              <span className="text-sm text-muted-foreground ml-2">{rating}/5</span>
            </div>
          </div>
          <div>
            <Label htmlFor="imagePath">Photo URL (optional)</Label>
            <Input id="imagePath" value={imagePath} onChange={(e) => setImagePath(e.target.value)} placeholder="https://..." />
            {imagePath && (
              <img src={imagePath} alt="Preview" className="mt-2 w-16 h-16 object-cover rounded-full border" />
            )}
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" id="featured" checked={featured} onChange={(e) => setFeatured(e.target.checked)} />
            <Label htmlFor="featured" className="cursor-pointer">Show on website (featured)</Label>
          </div>
          <div className="flex gap-3 pt-4">
            <Button type="submit" disabled={saving} style={{ borderRadius: "16px 0 16px 0" }}>
              {saving ? "Saving..." : isNew ? "Add Review" : "Save Changes"}
            </Button>
            <Button type="button" variant="outline" onClick={cancelEdit} style={{ borderRadius: "16px 0 16px 0" }}>
              Cancel
            </Button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-serif font-bold">
          Reviews ({reviews.filter((r) => r.featured).length} shown / {reviews.length} total)
        </h2>
        <div className="flex gap-3">
          <Button variant="outline" size="sm" onClick={fetchGoogleReviews} disabled={fetchingGoogle} style={{ borderRadius: "16px 0 16px 0" }}>
            <RefreshCw className={`w-4 h-4 mr-2 ${fetchingGoogle ? "animate-spin" : ""}`} />
            {fetchingGoogle ? "Syncing…" : "Fetch Google Reviews"}
          </Button>
          <Button size="sm" onClick={startNew} style={{ borderRadius: "16px 0 16px 0" }}>
            <Plus className="w-4 h-4 mr-2" /> Add Review
          </Button>
        </div>
      </div>

      {googleMessage && (
        <div className={`mb-4 p-3 text-sm rounded-md ${googleMessage.startsWith("Error") ? "bg-red-50 text-red-600" : "bg-blue-50 text-blue-700"}`}>
          {googleMessage}
        </div>
      )}

      {error && <div className="mb-4 p-3 bg-red-50 text-red-600 text-sm rounded-md">{error}</div>}

      <div className="space-y-3">
        {reviews.map((review, index) => (
          <div
            key={review.id}
            className={`bg-white rounded-lg shadow-sm p-4 border ${!review.featured ? "opacity-60" : ""}`}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-1">
                  {review.imagePath && (
                    <img src={review.imagePath} alt={review.name} className="w-10 h-10 rounded-full object-cover border" />
                  )}
                  <div>
                    <h3 className="font-bold text-sm">{review.name}</h3>
                    {review.subtitle && <p className="text-xs text-muted-foreground">{review.subtitle}</p>}
                  </div>
                  <div className="flex gap-0.5">
                    {[...Array(review.rating)].map((_, i) => (
                      <Star key={i} size={12} className="text-yellow-400" fill="currentColor" />
                    ))}
                  </div>
                  {review.source === "google" && (
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Google</span>
                  )}
                  {!review.featured && (
                    <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded">Hidden</span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground line-clamp-2 mt-1">{review.text}</p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => moveReview(index, "up")} disabled={index === 0} className="p-1 text-muted-foreground hover:text-foreground disabled:opacity-30" title="Move up">
                  <ArrowUp className="w-4 h-4" />
                </button>
                <button onClick={() => moveReview(index, "down")} disabled={index === reviews.length - 1} className="p-1 text-muted-foreground hover:text-foreground disabled:opacity-30" title="Move down">
                  <ArrowDown className="w-4 h-4" />
                </button>
                <button onClick={() => toggleFeatured(review)} className="p-1 text-muted-foreground hover:text-foreground" title={review.featured ? "Hide" : "Show"}>
                  {review.featured ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                </button>
                <button onClick={() => startEdit(review)} className="p-1 text-muted-foreground hover:text-foreground" title="Edit">
                  <Pencil className="w-4 h-4" />
                </button>
                <button onClick={() => handleDelete(review.id)} className="p-1 text-red-400 hover:text-red-600" title="Delete">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {reviews.length === 0 && (
        <div className="text-center py-12 text-muted-foreground">
          <p>No reviews yet.</p>
          <Button size="sm" onClick={startNew} className="mt-4" style={{ borderRadius: "16px 0 16px 0" }}>
            Add your first review
          </Button>
        </div>
      )}
    </div>
  );
}
